import React, {
    createContext,
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { AppEnv, getAppEnv, isProd as isProdEnv } from '../shared/appEnv';
import { isBrowser } from '../shared/Utils';
import {
    FeatureToggleName,
    IFeatureToggle,
} from './featureToggles.types';
import {
    getFeatureTogglesMap,
    getLocalFeatureTogglesByEnv,
    getSessionOverrideKey,
    isConfigToggle,
    isFeatureToggleEnabled,
} from './featureToggles.utils';
import {
    logFeatureFlagEvaluation,
    logFeatureToggleError,
} from './featureToggles.rum';
import { fetchRuntimeToggles } from './featureToggles.runtime';

export type MultiToggleSettings<T extends FeatureToggleName> = {
    [K in T]: boolean;
};

export interface IFeatureTogglesContext {
    /** Whether a toggle is on. Unknown names are `false`. */
    isFeatureToggledOn: (name: FeatureToggleName) => boolean;
    /** Several toggles at once, keyed by name so callers can destructure. */
    getMultiToggleSettings: <T extends FeatureToggleName>(
        names: T[],
    ) => MultiToggleSettings<T>;
    /**
     * Merge an external `{ name, isEnabled }` list over the build-time values
     * (env config and Drupal terms). `Config_` toggles in the list are ignored.
     * The provider calls this itself with the runtime file's values when
     * `Config_RuntimeToggleSource` is on.
     */
    updateToggles: (toggles: IFeatureToggle[]) => void;
    /**
     * False during the server render and first client render, when only the
     * build-time values (env config and Drupal terms) apply; true once runtime
     * values (the runtime file, when it is on, and sessionStorage overrides)
     * are applied. Wait for it before acting on a value, e.g. redirecting.
     */
    isResolved: boolean;
}

/**
 * Env config toggles for the current App_Env, as a map. On failure, logs and
 * returns an empty map (every toggle off) rather than crashing the page.
 */
const resolveEnvToggles = (
    appEnv: AppEnv,
): Map<FeatureToggleName, boolean> => {
    try {
        return getFeatureTogglesMap(getLocalFeatureTogglesByEnv(appEnv));
    } catch (error) {
        console.error(`Feature toggles: failed to resolve env config`, error);
        logFeatureToggleError(error);
        return new Map();
    }
};

const buildMultiToggleSettings = <T extends FeatureToggleName>(
    names: T[],
    isOn: (name: FeatureToggleName) => boolean,
): MultiToggleSettings<T> =>
    names.reduce(
        (settings, name) => ({ ...settings, [name]: isOn(name) }),
        {} as MultiToggleSettings<T>,
    );

// Without a provider (e.g. an isolated component test), fall back to the env
// config for the current App_Env with no overrides.
const fallbackMap = resolveEnvToggles(getAppEnv());
const fallbackIsOn = (name: FeatureToggleName) =>
    isFeatureToggleEnabled(name, fallbackMap, true);

export const FeatureTogglesContext = createContext<IFeatureTogglesContext>({
    isFeatureToggledOn: fallbackIsOn,
    getMultiToggleSettings: (names) =>
        buildMultiToggleSettings(names, fallbackIsOn),
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    updateToggles: () => {},
    isResolved: true,
});

interface FeatureTogglesProviderProps {
    children: ReactNode;
    /** App_Env to use. Defaults to the resolved one; set it in tests. */
    appEnv?: AppEnv;
    /**
     * Whether to load the runtime toggle file. Defaults to the env config's
     * `Config_RuntimeToggleSource` value; set it in tests.
     */
    runtimeSource?: boolean;
    /**
     * Toggles turned on in the Drupal Feature Flags taxonomy (see
     * `featureToggles.drupal.ts`), merged over the env config. They are fixed
     * at build time, so unlike the runtime file they apply to the server render
     * and hydration. `Config_` toggles in the list are ignored.
     */
    drupalToggles?: IFeatureToggle[];
}

/**
 * Computes the effective toggle values and shares them with the tree.
 *
 * Hydration safety: the server render and the first client render use only
 * the build-time values, the env config with the Drupal values over it.
 * sessionStorage overrides are applied after mount, which triggers an
 * ordinary re-render instead of a hydration mismatch. Consumers therefore need
 * no deferral of their own; they only need to tolerate a toggle changing value
 * after mount (a sessionStorage override in non-prod, or a runtime file value
 * when that source is on).
 *
 * Runtime file: when on, it is fetched after mount and its values are merged
 * through `updateToggles`, over the Drupal values. A missing or invalid file
 * changes nothing.
 */
export const FeatureTogglesProvider = ({
    children,
    appEnv,
    runtimeSource,
    drupalToggles,
}: FeatureTogglesProviderProps) => {
    const resolvedEnv = useMemo(() => appEnv ?? getAppEnv(), [appEnv]);
    const isProd = isProdEnv(resolvedEnv);

    const envToggles = useMemo(
        () => resolveEnvToggles(resolvedEnv),
        [resolvedEnv],
    );
    // Build-time values: Drupal terms over the env config.
    const buildToggles = useMemo(() => {
        const merged = new Map(envToggles);
        (drupalToggles ?? [])
            .filter(({ name }) => !isConfigToggle(name))
            .forEach(({ name, isEnabled }) => merged.set(name, isEnabled));
        return merged;
    }, [envToggles, drupalToggles]);
    const [externalToggles, setExternalToggles] = useState<
        Map<FeatureToggleName, boolean>
    >(() => new Map());
    const [hasMounted, setHasMounted] = useState(false);

    useEffect(() => setHasMounted(true), []);

    const isRuntimeSourceOn =
        runtimeSource ??
        envToggles.get(FeatureToggleName.Config_RuntimeToggleSource) ??
        false;
    const [isRuntimeSettled, setIsRuntimeSettled] = useState(
        !isRuntimeSourceOn,
    );

    const updateToggles = useCallback((toggles: IFeatureToggle[]) => {
        setExternalToggles((previous) => {
            const next = new Map(previous);
            toggles
                .filter(({ name }) => !isConfigToggle(name))
                .forEach(({ name, isEnabled }) => next.set(name, isEnabled));
            return next;
        });
    }, []);

    // Runtime file: fetched after mount only, so it never affects the static
    // HTML or hydration. fetchRuntimeToggles never rejects.
    useEffect(() => {
        if (!isRuntimeSourceOn) {
            setIsRuntimeSettled(true);
            return undefined;
        }
        let isCancelled = false;
        setIsRuntimeSettled(false);
        fetchRuntimeToggles().then((toggles) => {
            if (isCancelled) return;
            if (toggles.length) updateToggles(toggles);
            setIsRuntimeSettled(true);
        });
        return () => {
            isCancelled = true;
        };
    }, [isRuntimeSourceOn, updateToggles]);

    const effectiveToggles = useMemo(() => {
        const merged = new Map(buildToggles);
        externalToggles.forEach((value, name) => merged.set(name, value));
        return merged;
    }, [buildToggles, externalToggles]);

    // Non-prod: seed sessionStorage so every overridable toggle is visible and
    // editable in dev tools. Existing keys are left alone so a developer's
    // override survives navigation. Seeding waits for the runtime file, so a
    // seeded key holds the runtime value rather than shadowing it. Production
    // never touches sessionStorage.
    useEffect(() => {
        if (isProd || !isBrowser || !isRuntimeSettled) return;
        try {
            effectiveToggles.forEach((value, name) => {
                if (isConfigToggle(name)) return;
                const key = getSessionOverrideKey(name);
                if (window.sessionStorage.getItem(key) === null) {
                    window.sessionStorage.setItem(key, String(value));
                }
            });
        } catch {
            // sessionStorage unavailable; overrides are simply not offered.
        }
    }, [isProd, isRuntimeSettled, effectiveToggles]);

    // Overrides are skipped until mount (see hydration note above).
    const ignoreOverrides = isProd || !hasMounted;

    const isFeatureToggledOn = useCallback(
        (name: FeatureToggleName) => {
            const value = isFeatureToggleEnabled(
                name,
                effectiveToggles,
                ignoreOverrides,
            );
            logFeatureFlagEvaluation(name, value);
            return value;
        },
        [effectiveToggles, ignoreOverrides],
    );

    const getMultiToggleSettings = useCallback(
        <T extends FeatureToggleName>(names: T[]) =>
            buildMultiToggleSettings(names, isFeatureToggledOn),
        [isFeatureToggledOn],
    );

    const isResolved = hasMounted && isRuntimeSettled;

    const value = useMemo(
        () => ({
            isFeatureToggledOn,
            getMultiToggleSettings,
            updateToggles,
            isResolved,
        }),
        [isFeatureToggledOn, getMultiToggleSettings, updateToggles, isResolved],
    );

    return (
        <FeatureTogglesContext.Provider value={value}>
            {children}
        </FeatureTogglesContext.Provider>
    );
};
